/**
 * Create / edit a style, in the LifeScribe original's dialog shell
 * (AdminEditDialogShell + PromptField) with its Complete Prompt / Structured
 * Components tab pair.
 *
 * Differences from the original, all deliberate:
 *
 *   • The dialog is bound to the CATALOGUE THE ADMIN HAS OPEN. The original
 *     always created a writing genre or an illustration style regardless of the
 *     tab, so "Add" from the Authors tab quietly produced a genre.
 *   • The illustration prompt fields (visual approach, prompt construction) are
 *     persisted. The original collected them and dropped them on submit.
 *   • NEW: "LifeScribe style key" (`ls_style_key`). LifeScribe's transformer
 *     resolves a style BY KEY, so a style offered to members without one cannot
 *     actually be applied — the backend refuses to activate it (400
 *     HONESTY_GATE). The helper text says so in plain language.
 *   • NEW: "Unavailable reason" — the admin-visible explanation for a style that
 *     is deliberately withheld.
 *   • Saving routes through QuillActionConfirm, like every other Quill
 *     production write (see interview/QuestionEditDialog for the precedent).
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  FormControlLabel,
  Grid,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import AdminEditDialogShell from '../../shared/AdminEditDialogShell';
import PromptField from '../../shared/PromptField';
import QuillActionConfirm from '../common/QuillActionConfirm';
import {
  ACCENT_ILLUSTRATION,
  ACCENT_WRITING,
  ILLUSTRATION_DIALOG_SUBTITLE,
  KEY_FIELD,
  PRODUCTION_CAPTION,
} from './constants';
import { StyleRow, StyleWritePayload, TabConfig } from './types';

export interface StyleEditDialogProps {
  open: boolean;
  tab: TabConfig;
  /** null → create in `tab.catalogue`. */
  editing: StyleRow | null;
  /** Identity of the row being edited (URL key). Empty when creating. */
  editingKey: string;
  saving: boolean;
  onClose: () => void;
  onSubmit: (payload: StyleWritePayload) => void;
}

interface FormState {
  key: string;
  display_name: string;
  description: string;
  display_description: string;
  sample_excerpt: string;
  style_dna: string;
  transformation_formula: string;
  visual_approach: string;
  prompt_construction: string;
  truth_integration: string;
  ls_style_key: string;
  unavailable_reason: string;
  is_premium: boolean;
  is_active: boolean;
}

const BLANK: FormState = {
  key: '',
  display_name: '',
  description: '',
  display_description: '',
  sample_excerpt: '',
  style_dna: '',
  transformation_formula: '',
  visual_approach: '',
  prompt_construction: '',
  truth_integration: '',
  ls_style_key: '',
  unavailable_reason: '',
  is_premium: false,
  is_active: true,
};

function fromRow(row: StyleRow, key: string): FormState {
  return {
    key,
    display_name: row.display_name || '',
    description: row.description || '',
    display_description: row.display_description || '',
    sample_excerpt: row.sample_excerpt || '',
    style_dna: row.style_dna || '',
    transformation_formula: row.transformation_formula || '',
    visual_approach: row.visual_approach || '',
    prompt_construction: row.prompt_construction || '',
    truth_integration: row.truth_integration || '',
    ls_style_key: row.ls_style_key || '',
    unavailable_reason: row.unavailable_reason || '',
    is_premium: Boolean(row.is_premium),
    is_active: Boolean(row.is_active),
  };
}

/** Read-only assembly of the stored components — the same sectioning the engine sees. */
export function assemblePrompt(form: FormState, family: 'writing' | 'illustration'): string {
  const sections: string[] = [];
  if (form.style_dna) sections.push(`[STYLE DNA]\n${form.style_dna}`);
  if (family === 'writing') {
    if (form.transformation_formula) {
      sections.push(`[TRANSFORMATION FORMULA]\n${form.transformation_formula}`);
    }
  } else {
    if (form.visual_approach) sections.push(`[VISUAL APPROACH]\n${form.visual_approach}`);
    if (form.prompt_construction) {
      sections.push(`[PROMPT CONSTRUCTION]\n${form.prompt_construction}`);
    }
  }
  if (form.truth_integration) sections.push(`[TRUTH INTEGRATION]\n${form.truth_integration}`);
  return sections.join('\n\n');
}

const StyleEditDialog: React.FC<StyleEditDialogProps> = ({
  open,
  tab,
  editing,
  editingKey,
  saving,
  onClose,
  onSubmit,
}) => {
  const isEdit = editing != null;
  const family = tab.family;
  const accent = family === 'writing' ? ACCENT_WRITING : ACCENT_ILLUSTRATION;

  const [form, setForm] = useState<FormState>(BLANK);
  const [dirty, setDirty] = useState(false);
  const [promptTab, setPromptTab] = useState(1);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(editing ? fromRow(editing, editingKey) : BLANK);
    setDirty(false);
    setPromptTab(1);
    setShowErrors(false);
    setConfirmOpen(false);
  }, [open, editing, editingKey]);

  const set = <K extends keyof FormState>(field: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setDirty(true);
  };

  const keyFieldLabel = KEY_FIELD[tab.catalogue].replace(/_/g, ' ');
  const keyMissing = !isEdit && form.key.trim().length === 0;
  const nameMissing = form.display_name.trim().length === 0;
  const canSubmit = !keyMissing && !nameMissing;

  const assembled = useMemo(() => assemblePrompt(form, family), [form, family]);

  // Reachable two ways: the footer button (via the shell's onSave) and Enter
  // inside the form. The shell prefers `formId` when given one, which relies on
  // implicit form submission from a button outside the <form>; wiring onSave
  // directly keeps the footer button working the same way in every environment.
  const requestSave = (e?: React.FormEvent) => {
    e?.preventDefault();
    setShowErrors(true);
    if (!canSubmit) return;
    setConfirmOpen(true);
  };

  const confirmSave = () => {
    onSubmit({
      key: form.key.trim(),
      display_name: form.display_name.trim(),
      description: form.description,
      display_description: form.display_description,
      sample_excerpt: form.sample_excerpt,
      style_dna: form.style_dna,
      transformation_formula: form.transformation_formula,
      visual_approach: form.visual_approach,
      prompt_construction: form.prompt_construction,
      truth_integration: form.truth_integration,
      ls_style_key: form.ls_style_key.trim() ? form.ls_style_key.trim() : null,
      unavailable_reason: form.unavailable_reason.trim() ? form.unavailable_reason.trim() : null,
      is_premium: form.is_premium,
      is_active: form.is_active,
    });
    setConfirmOpen(false);
  };

  return (
    <>
      <AdminEditDialogShell
        open={open}
        onClose={onClose}
        accentColor={accent}
        typeInitial={family === 'writing' ? 'W' : 'I'}
        title={`${isEdit ? 'Edit' : 'Create'} ${tab.noun} — ${form.display_name || 'Untitled'}`}
        subtitle={
          family === 'writing'
            ? 'Components assemble into the transformation prompt the engine sends'
            : // The constant, NOT a second hand-typed copy: this line used to
              // repeat the illustration banner verbatim, which is how the two
              // claims about one code path drift apart.
              ILLUSTRATION_DIALOG_SUBTITLE
        }
        onSave={() => requestSave()}
        isDirty={dirty}
        isSaving={saving}
        saveLabel={isEdit ? 'Save changes' : `Create ${tab.noun}`}
        maxWidth="lg"
        showActiveToggle
        isActive={form.is_active}
        onToggleActive={() => set('is_active', !form.is_active)}
      >
        <form id="quill-style-edit-form" onSubmit={requestSave}>
          <Box sx={{ p: 3 }}>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label={keyFieldLabel}
                  value={form.key}
                  onChange={(e) => set('key', e.target.value)}
                  fullWidth
                  disabled={isEdit}
                  error={showErrors && keyMissing}
                  helperText={
                    isEdit
                      ? 'The identity column — immutable once the row exists.'
                      : 'Stable identifier for this style, e.g. warm_memoir. Required.'
                  }
                  inputProps={{ 'aria-label': keyFieldLabel }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Display name"
                  value={form.display_name}
                  onChange={(e) => set('display_name', e.target.value)}
                  fullWidth
                  error={showErrors && nameMissing}
                  helperText={showErrors && nameMissing ? 'A display name is required.' : ' '}
                  inputProps={{ 'aria-label': 'Display name' }}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  label="Description (internal notes)"
                  value={form.description}
                  onChange={(e) => set('description', e.target.value)}
                  fullWidth
                  multiline
                  rows={3}
                  helperText="Admin-facing. Never shown to members."
                  inputProps={{ 'aria-label': 'Description (internal notes)' }}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  label="Display description (shown to members)"
                  value={form.display_description}
                  onChange={(e) => set('display_description', e.target.value)}
                  fullWidth
                  multiline
                  rows={2}
                  helperText="Short, member-facing blurb rendered in the app."
                  inputProps={{ 'aria-label': 'Display description (shown to members)' }}
                />
              </Grid>

              {family === 'writing' && (
                <Grid size={{ xs: 12 }}>
                  <TextField
                    label="Sample excerpt (flip-card preview)"
                    value={form.sample_excerpt}
                    onChange={(e) => set('sample_excerpt', e.target.value)}
                    fullWidth
                    multiline
                    rows={5}
                    inputProps={{ maxLength: 800, 'aria-label': 'Sample excerpt (flip-card preview)' }}
                    helperText={`A short passage demonstrating this style, shown on the card back. ${form.sample_excerpt.length}/800 characters`}
                  />
                </Grid>
              )}

              {/* The honesty gate, in plain language. */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="LifeScribe style key"
                  value={form.ls_style_key}
                  onChange={(e) => set('ls_style_key', e.target.value)}
                  fullWidth
                  inputProps={{ 'aria-label': 'LifeScribe style key' }}
                  helperText="LifeScribe's transformer resolves a style BY KEY, so a style offered to members must carry one. Leave it blank and the backend will refuse to activate this row."
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Unavailable reason"
                  value={form.unavailable_reason}
                  onChange={(e) => set('unavailable_reason', e.target.value)}
                  fullWidth
                  inputProps={{ 'aria-label': 'Unavailable reason' }}
                  helperText="Why this style is deliberately withheld. Shown to admins on the card."
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                  <Tabs
                    value={promptTab}
                    onChange={(_e, v) => setPromptTab(v)}
                    aria-label="Prompt editing mode"
                  >
                    <Tab label="Complete Prompt" />
                    <Tab label="Structured Components" />
                  </Tabs>
                </Box>

                {promptTab === 0 && (
                  <Box>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Read-only preview, assembled from the components on the next tab. Nothing here
                      is stored separately.
                    </Alert>
                    <PromptField
                      value={assembled}
                      onChange={() => undefined}
                      label="ASSEMBLED PROMPT (PREVIEW)"
                      accentColor={accent}
                      minRows={10}
                      readOnly
                    />
                  </Box>
                )}

                {promptTab === 1 && (
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12 }}>
                      <PromptField
                        value={form.style_dna}
                        onChange={(v) => set('style_dna', v)}
                        label="STYLE DNA"
                        helperText="Core essence and defining characteristics of this style."
                        accentColor={accent}
                        recommendation="200–500 chars recommended"
                        minRows={8}
                      />
                    </Grid>

                    {family === 'writing' && (
                      <Grid size={{ xs: 12 }}>
                        <PromptField
                          value={form.transformation_formula}
                          onChange={(v) => set('transformation_formula', v)}
                          label="TRANSFORMATION FORMULA"
                          helperText="How a member's own words are transformed into this style."
                          accentColor={accent}
                          recommendation="300–600 chars recommended"
                          minRows={8}
                        />
                      </Grid>
                    )}

                    {family === 'illustration' && (
                      <>
                        <Grid size={{ xs: 12 }}>
                          <PromptField
                            value={form.visual_approach}
                            onChange={(v) => set('visual_approach', v)}
                            label="VISUAL APPROACH"
                            helperText="How visual elements and composition should be interpreted."
                            accentColor={accent}
                            recommendation="200–500 chars recommended"
                            minRows={8}
                          />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                          <PromptField
                            value={form.prompt_construction}
                            onChange={(v) => set('prompt_construction', v)}
                            label="PROMPT CONSTRUCTION"
                            helperText="Technical instructions for building the image prompt. Stored and returned by the API — unlike LifeScribe's editor, this field is persisted."
                            accentColor={accent}
                            recommendation="300–600 chars recommended"
                            minRows={8}
                          />
                        </Grid>
                      </>
                    )}

                    <Grid size={{ xs: 12 }}>
                      <PromptField
                        value={form.truth_integration}
                        onChange={(v) => set('truth_integration', v)}
                        label="TRUTH INTEGRATION"
                        helperText="How the member's own truth is woven into the output."
                        accentColor={accent}
                        recommendation="200–400 chars recommended"
                        minRows={8}
                      />
                    </Grid>
                  </Grid>
                )}
              </Grid>

              <Grid size={{ xs: 12 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.is_premium}
                      onChange={(_e, checked) => set('is_premium', checked)}
                      slotProps={{ input: { 'aria-label': 'Premium style' } }}
                    />
                  }
                  label="Premium style (requires subscription)"
                />
                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                  {PRODUCTION_CAPTION}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </form>
      </AdminEditDialogShell>

      <QuillActionConfirm
        open={confirmOpen}
        title={isEdit ? `Save ${tab.noun}` : `Create ${tab.noun}`}
        message={
          <>
            {isEdit ? 'Save changes to ' : 'Create '}
            <strong>{form.display_name || form.key}</strong> in the{' '}
            <strong>{tab.label}</strong> catalogue.
          </>
        }
        detail={
          !form.ls_style_key.trim() && form.is_active ? (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              This style has no LifeScribe style key, so the backend will refuse to keep it active.
            </Typography>
          ) : undefined
        }
        caption={PRODUCTION_CAPTION}
        confirmLabel={isEdit ? 'Save changes' : 'Create style'}
        loading={saving}
        pendingLabel="Saving…"
        onConfirm={confirmSave}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
};

export default StyleEditDialog;
